"use client";

import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { FC, useMemo, useState, useEffect, useLayoutEffect, useRef, type CSSProperties } from "react";
import * as z from "@/lib/propSchema";
import Image from "next/image";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import { useIsInView } from "@/hooks/useIsInView";

/** Matches `--breakpoint-sm`…`--breakpoint-xl` in `globals.css` (see Banner3). */
const LEADERS1_BP = { sm: 1080, md: 1230, lg: 1430, xl: 1750 } as const;

export type Leaders1Band = "xs" | "sm" | "md" | "lg" | "xl";

function getBandFromWidth(w: number): Leaders1Band {
    if (w >= LEADERS1_BP.xl) return "xl";
    if (w >= LEADERS1_BP.lg) return "lg";
    if (w >= LEADERS1_BP.md) return "md";
    if (w >= LEADERS1_BP.sm) return "sm";
    return "xs";
}

function useLeaders1Band(): Leaders1Band {
    const [band, setBand] = useState<Leaders1Band>("xs");
    useLayoutEffect(() => {
        const u = () => setBand(getBandFromWidth(window.innerWidth));
        u();
        window.addEventListener("resize", u);
        return () => window.removeEventListener("resize", u);
    }, []);
    return band;
}

/** Figma: 598:3840 (xs), 598:3841 (xs purple block h), 761:5844+5845 (sm), … */
const LEADERS1_LAYOUT: Record<
    Leaders1Band,
    {
        stageMinH: number;
        centerW: number;
        centerH: number;
        sideW: number;
        sideH: number;
        sideOffsetX: number;
        sideOffsetY: number;
        sideBlur: number;
        sideOpacity: number;
        textGradientH: number;
        /** Half of `centerH`: cards are centered on the stage’s top, so this much projects above the stage. */
        xsCardBleedUp: number;
        /** Space above the learn block on mobile so cards don’t cover the section (pre)title. */
        xsSectionHeaderClearance: number;
        /** Full purple “learn from leaders” column height on mobile (Figma: stage frame 477). */
        mobilePurpleH: number;
        /** Gap (px) between the center card’s bottom and the copy block. */
        mobileDescriptionGapAfterCards: number;
        /** Pull the desktop learn row up (tighter to the section title). */
        desktopBlockLift: number;
        /** Nudge sm–xl left/right (flanking) cards up by this many px. */
        desktopFlankUpPx: number;
        panelStripH: number;
        /** Figma: left padding from purple strip edge to description column (sm–xl). */
        desktopTextPl: number;
        /**
         * Figma: max width (px) of the learn column and of the title text layer
         * (598:3844, 761:5847, 724:5731, 722:5594, 724:5661).
         */
        desktopDescriptionMaxW: number;
        panelRadius: number;
    }
> = {
    xs: {
        stageMinH: 477,
        centerW: 226.085,
        centerH: 330,
        sideW: 152,
        sideH: 220,
        sideOffsetX: 101,
        sideOffsetY: 55,
        sideBlur: 4.34,
        sideOpacity: 0.92,
        textGradientH: 120.762,
        xsCardBleedUp: 165,
        /** ceil(centerH/2) — keeps main titles clear of the center card. */
        xsSectionHeaderClearance: 165,
        mobilePurpleH: 477,
        mobileDescriptionGapAfterCards: 20,
        desktopBlockLift: 0,
        desktopFlankUpPx: 0,
        panelStripH: 0,
        desktopTextPl: 0,
        desktopDescriptionMaxW: 303,
        panelRadius: 0,
    },
    sm: {
        stageMinH: 496,
        centerW: 230,
        centerH: 340,
        sideW: 180,
        sideH: 260,
        sideOffsetX: 133,
        sideOffsetY: 42,
        sideBlur: 5.06,
        sideOpacity: 0.92,
        textGradientH: 124,
        xsCardBleedUp: 0,
        xsSectionHeaderClearance: 0,
        mobilePurpleH: 0,
        mobileDescriptionGapAfterCards: 0,
        desktopBlockLift: 150,
        desktopFlankUpPx: 28,
        panelStripH: 220,
        desktopTextPl: 60,
        desktopDescriptionMaxW: 500,
        panelRadius: 30,
    },
    md: {
        stageMinH: 580,
        centerW: 274,
        centerH: 400,
        sideW: 213,
        sideH: 309,
        sideOffsetX: 176,
        sideOffsetY: 46,
        sideBlur: 5.98,
        sideOpacity: 0.92,
        textGradientH: 147,
        xsCardBleedUp: 0,
        xsSectionHeaderClearance: 0,
        mobilePurpleH: 0,
        mobileDescriptionGapAfterCards: 0,
        desktopBlockLift: 150,
        desktopFlankUpPx: 28,
        panelStripH: 246,
        desktopTextPl: 50,
        desktopDescriptionMaxW: 450,
        panelRadius: 33.438,
    },
    lg: {
        stageMinH: 685,
        centerW: 322,
        centerH: 470,
        sideW: 245,
        sideH: 355,
        sideOffsetX: 184,
        sideOffsetY: 58,
        sideBlur: 7,
        sideOpacity: 0.92,
        textGradientH: 171.994,
        xsCardBleedUp: 0,
        xsSectionHeaderClearance: 0,
        mobilePurpleH: 0,
        mobileDescriptionGapAfterCards: 0,
        desktopBlockLift: 200,
        desktopFlankUpPx: 28,
        panelStripH: 293,
        desktopTextPl: 59,
        desktopDescriptionMaxW: 508,
        panelRadius: 40,
    },
    xl: {
        stageMinH: 730,
        centerW: 350,
        centerH: 505,
        sideW: 274,
        sideH: 396,
        sideOffsetX: 205,
        sideOffsetY: 54,
        sideBlur: 7.83,
        sideOpacity: 0.92,
        textGradientH: 167,
        xsCardBleedUp: 0,
        xsSectionHeaderClearance: 0,
        mobilePurpleH: 0,
        mobileDescriptionGapAfterCards: 0,
        desktopBlockLift: 200,
        desktopFlankUpPx: 28,
        panelStripH: 293,
        desktopTextPl: 70,
        desktopDescriptionMaxW: 619,
        panelRadius: 40,
    },
};

/** Figma (598:3843–3845, 761:5846–48, 724:5730–32, 722:5593–95, 724:5660–62): learn-block title + body type. */
const LEADERS1_DESC_TYPO: Record<
    Leaders1Band,
    {
        titleGap: number;
        preTitle: { className: string };
        title: { className: string };
        body: { className: string };
    }
> = {
    xs: {
        titleGap: 16,
        preTitle: {
            className:
                "text-[24px] font-semibold leading-[1.4] tracking-[0.24px] not-italic",
        },
        title: { className: "text-[24px] font-extrabold italic leading-[1.4] tracking-[0.24px]" },
        body: { className: "text-[14px] font-semibold leading-[1.49] not-italic" },
    },
    sm: {
        titleGap: 16,
        preTitle: {
            className:
                "text-[24px] font-semibold leading-[1.4] tracking-[0.24px] not-italic",
        },
        title: { className: "text-[24px] font-extrabold italic leading-[1.4] tracking-[0.24px]" },
        body: { className: "text-[14px] font-semibold leading-[1.49] not-italic" },
    },
    md: {
        titleGap: 20,
        preTitle: {
            className:
                "text-[24px] font-semibold leading-[1.4] tracking-[0.24px] not-italic",
        },
        title: { className: "text-[24px] font-extrabold italic leading-[1.4] tracking-[0.24px]" },
        body: { className: "text-[16px] font-semibold leading-[1.49] not-italic tracking-[0.16px]" },
    },
    lg: {
        titleGap: 24,
        preTitle: {
            className:
                "text-[28px] font-semibold leading-[1.4] tracking-[0.28px] not-italic",
        },
        title: { className: "text-[28px] font-extrabold italic leading-[1.4] tracking-[0.28px]" },
        body: { className: "text-[18px] font-semibold leading-[1.49] not-italic tracking-[0.18px]" },
    },
    xl: {
        titleGap: 24,
        preTitle: {
            className:
                "text-[28px] font-semibold leading-[1.4] tracking-[0.28px] not-italic",
        },
        title: { className: "text-[28px] font-extrabold italic leading-[1.4] tracking-[0.28px]" },
        body: { className: "text-[18px] font-semibold leading-[1.49] not-italic tracking-[0.18px]" },
    },
};

const Leaders1MetadataItemSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    meta_icon: z.string().optional(),
    meta_image: z.string().optional(),
    meta_title: z.string().optional(),
    meta_status: z.number().optional().default(1),
    meta_alt_icon: z.string().optional(),
    meta_priority: z.coerce.number().optional().default(1),
    meta_alt_image: z.string().optional(),
    meta_sub_title: z.string().optional(),
    meta_title_color: z.string().optional(),
    meta_sub_title_color: z.string().optional(),
});

const Leaders1DataSchema = z
    .object({
        id: z.union([z.number(), z.string()]).optional(),
        page_id: z.union([z.number(), z.string()]).optional(),
        component_id: z.union([z.number(), z.string()]).optional(),
        desktop_background_color: z.string().optional(),
        mobile_background_color: z.string().optional(),
        backvariant: z.enum(["variant_1", "variant_2", "variant_3"]).optional().default("variant_1"),
        gradient_color_1: z.string().optional(),
        gradient_color_2: z.string().optional(),
        gradient_color_3: z.string().optional(),
        gradient_color_4: z.string().optional(),
        pre_title: z.string().optional(),
        pre_title_color: z.string().optional(),
        title: z.string().optional(),
        title_start_color: z.string().optional(),
        title_end_color: z.string().optional(),
        title_gradient_angle: z.string().optional(),
        scroll_frequency: z.coerce
            .number()
            .optional()
            .default(3000)
            .transform((v) => (v > 0 ? v : 3000)),
        description_box_start_color: z.string().optional(),
        description_box_end_color: z.string().optional(),
        description_box_gradient_angle: z.string().optional(),
        description_section_pre_title: z.string().optional(),
        description_section_pre_title_color: z.string().optional(),
        description_section_title: z.string().optional(),
        description_section_title_color: z.string().optional(),
        description_section_description: z.string().optional(),
        description_section_description_text_color: z.string().optional(),
        variant: z.string().optional().default("1"),
        metadata: z.array(Leaders1MetadataItemSchema).optional().default([]),
        status: z.union([z.number(), z.boolean()]).optional().default(1).transform(v => v === true || v === 1 ? true : false),
        enable_animation: z.union([z.boolean(), z.number()]).optional().default(false),
        is_deleted: z.number().optional().default(0),
        created_by: z.coerce.string().optional(),
        updated_by: z.coerce.string().optional(),
        createdAt: z.string().optional(),
        updatedAt: z.string().optional(),
    })
    .transform((data) => {
        return {
            ...data,
            metadata: data.metadata?.sort((a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0)),
        };
    });

type Leaders1MetadataItem = z.infer<typeof Leaders1MetadataItemSchema>;
type Leaders1Parsed = z.infer<typeof Leaders1DataSchema>;

const LeaderCard: FC<{
    item: Leaders1MetadataItem;
    position: number;
    band: Leaders1Band;
    className?: string;
}> = ({ item, position, band, className }) => {
    const L = LEADERS1_LAYOUT[band];
    const hasImage = item.meta_image && item.meta_image.trim() !== "";
    const hasIcon = item.meta_icon && item.meta_icon.trim() !== "";
    const isActive = position === 0;
    const isLeft = position === -1;
    const isRight = position === 1;
    const isHidden = position === 2;

    const w = isActive || isHidden ? L.centerW : L.sideW;
    const h = isActive || isHidden ? L.centerH : L.sideH;
    const rounded =
        band === "xs"
            ? 19.169
            : band === "sm"
              ? 19.749
              : band === "md"
                ? 24
                : 27.301;

    /** Mobile (xs): center card’s center is pinned to the top-center of the positioning context (the purple mobile block). */
    const getTransform = (): string => {
        if (band === "xs") {
            if (isHidden) {
                return "translate3d(-50%, -50%, 0) scale(0.5) translateY(40px)";
            }
            if (isActive) {
                return "translate3d(-50%, -50%, 0) scale(1)";
            }
            if (isLeft) {
                return `translate3d(calc(-50% - ${L.sideOffsetX}px), -50%, 0) scale(1)`;
            }
            if (isRight) {
                return `translate3d(calc(-50% + ${L.sideOffsetX}px), -50%, 0) scale(1)`;
            }
            return "translate3d(-50%, -50%, 0) scale(1)";
        }
        /** sm–xl: pin each card to the **vertical midline of the host** (same as the purple strip’s center line, since the strip is centered in this row). */
        if (isHidden) {
            return "translate3d(-50%, -50%, 0) scale(0.55) translateY(48px)";
        }
        if (isActive) {
            return "translate3d(-50%, -50%, 0) scale(1)";
        }
        if (isLeft) {
            const y = L.sideOffsetY - L.desktopFlankUpPx;
            return `translate3d(calc(-50% - ${L.sideOffsetX}px), calc(-50% + ${y}px), 0) scale(1)`;
        }
        if (isRight) {
            const y = L.sideOffsetY - L.desktopFlankUpPx;
            return `translate3d(calc(-50% + ${L.sideOffsetX}px), calc(-50% + ${y}px), 0) scale(1)`;
        }
        return "translate3d(-50%, -50%, 0) scale(1)";
    };

    const textStripH = isActive ? L.textGradientH : L.textGradientH * (L.sideH / L.centerH);
    /** Taller than the text block so the frosted blur extends further up the photo. */
    const textStripFrostH = textStripH + 80;
    const textStripPadTop = band === "xs" ? 27.5 : band === "sm" ? 30 : band === "md" ? 35 : band === "lg" ? 39.16 : 30;

    const style: CSSProperties = {
        left: "50%",
        top: band === "xs" ? 0 : "50%",
        width: w,
        height: h,
        borderRadius: rounded,
        transform: getTransform(),
        transformOrigin: "center center",
        filter: isActive ? "blur(0px)" : isHidden ? "blur(10px)" : `blur(${L.sideBlur}px)`,
        zIndex: isActive ? 30 : isHidden ? 0 : 20,
        opacity: isActive ? 1 : isHidden ? 0 : L.sideOpacity,
        ...(hasImage
            ? {
                  backgroundImage: `url(${item.meta_image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
              }
            : { backgroundColor: "#1a1a2e" }),
    };

    return (
        <div
            className={cn(
                "absolute transition-all duration-500 ease-in-out",
                "flex flex-col overflow-hidden bg-white",
                "shadow-[0_4px_20px_rgba(0,0,0,0.08)]",
                className
            )}
            style={style}
        >
            <div className="absolute inset-0 z-[2] flex h-full w-full flex-col-reverse overflow-hidden pointer-events-none *:pointer-events-auto">
                <div
                    className="relative inset-x-0 bottom-0 w-full shrink-0"
                    style={{ height: textStripFrostH }}
                >
                    {/*
                      Bottom frosted layer (match ProgramBenefits-style top slot): same backdrop stack, mask
                      fades toward the top so the strip sits behind the title block without a black gradient.
                    */}
                    <div
                        className={cn(
                            "absolute inset-0 z-0 pointer-events-none",
                            "backdrop-blur-[30px]",
                            "backdrop-saturate-[110%]",
                            "backdrop-brightness-10",
                            "[mask-image:linear-gradient(to_top,black_0%,black_40%,rgba(0,0,0,0.2)_70%,transparent_100%)]"
                        )}
                    />
                    <div
                        className="relative z-10 flex h-full w-full min-h-0 flex-col items-center justify-end px-6 pb-6"
                        style={{ paddingTop: textStripPadTop }}
                    >
                    <p
                        className={cn(
                            "w-full truncate text-center font-semibold text-white",
                            band === "xs" && "text-[15.335px] leading-[140%]",
                            band === "sm" && "text-[16px] leading-[140%]",
                            band === "md" && "text-[18px] leading-[140%]",
                            band === "lg" && "text-[21.841px] leading-[140%]",
                            band === "xl" && "text-[22px] leading-[140%]"
                        )}
                        style={{ color: item.meta_title_color || "#FFFFFF" }}
                    >
                        {item.meta_title || ""}
                    </p>
                    <div
                        className={cn(
                            "my-1 h-px w-8 rounded-full bg-white/80",
                            band === "xs" && "my-[9.58px]",
                            band === "sm" && "my-[10px]",
                            band === "md" && "my-[12px]",
                            band === "lg" && "my-[13.65px]",
                            band === "xl" && "my-[14px]"
                        )}
                        aria-hidden
                    />
                    <p
                        className={cn(
                            "w-full truncate text-center font-medium italic text-white/90",
                            band === "xs" && "text-[11.501px] leading-[140%]",
                            band === "sm" && "text-[12px] leading-[140%]",
                            band === "md" && "text-[14px] leading-[140%]",
                            band === "lg" && "text-[16.38px] leading-[140%]",
                            band === "xl" && "text-[16px] leading-[140%]"
                        )}
                        style={{ color: item.meta_sub_title_color || "rgba(255,255,255,0.9)" }}
                    >
                        {item.meta_sub_title || ""}
                    </p>
                    {hasIcon && (
                        <div className="relative mt-3 flex items-center justify-center">
                            <Image
                                src={item.meta_icon!}
                                alt={item.meta_alt_icon || ""}
                                width={80}
                                height={24}
                                className={cn(
                                    "object-contain object-center",
                                    band === "xs" && "h-[15.342] w-[115.97px]",
                                    band === "sm" && "h-[16.14] w-auto",
                                    band === "md" && "h-[18px] w-[140px]",
                                    band === "lg" && "h-[21.85] w-[165.169px]",
                                    band === "xl" && "h-[20px] w-[165px]"
                                )}
                            />
                        </div>
                    )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const Leaders1SectionHeader: FC<{ data: Leaders1Parsed }> = ({ data }) => {
    const isAnimationEnabled =
        data.enable_animation === true || data.enable_animation === 1;

    // One <h2> for both lines, so the section has a real heading. The reveals render as
    // spans (a heading may only hold phrasing content); the flex column blockifies them.
    return (
    <h2 className="relative mb-[24px] flex w-full min-w-0 flex-col text-left">
        <RevealOnScroll as="span" enableAnimation={isAnimationEnabled}>
        <span
            className={cn(
                "white-title-wf text-left font-semibold leading-normal",
                "line-clamp-1 sm:line-clamp-none sm:whitespace-nowrap"
            )}
            style={{
                color: data.pre_title_color,
                fontFamily: "var(--font-google-sans)",
            }}
        >
            {data.pre_title}
        </span>
        </RevealOnScroll>

        <RevealOnScroll as="span" delay={100} enableAnimation={isAnimationEnabled}>
        <span
            className={cn(
                "gradient-title-denton line-clamp-2 wrap-break-word bg-clip-text font-bold leading-[1.2] whitespace-pre-wrap",
                "sm:wrap-normal sm:line-clamp-1"
            )}
            style={{
                backgroundImage:
                    data.title_gradient_angle && data.title_start_color && data.title_end_color
                        ? `linear-gradient(${data.title_gradient_angle}, ${data.title_start_color}, ${data.title_end_color})`
                        : undefined,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontFamily: "var(--font-merriweather)",
            }}
        >
            {data.title}
        </span>
        </RevealOnScroll>
    </h2>
    );
};

/** Shared “Learn from leaders…” + body copy. */
const Leaders1DescriptionCopy: FC<{
    data: Leaders1Parsed;
    band: Leaders1Band;
    className?: string;
    titleWrapperClassName?: string;
    descriptionClassName?: string;
    /** `center` = mobile; `left` = desktop. */
    textAlign?: "center" | "left";
}> = ({ data, band, className, titleWrapperClassName, descriptionClassName, textAlign = "left" }) => {
    const t = textAlign === "center" ? "text-center" : "text-left";
    const T = LEADERS1_DESC_TYPO[band];
    const titleTextMaxW = LEADERS1_LAYOUT[band].desktopDescriptionMaxW;
    const isAnimationEnabled =
        data.enable_animation === true || data.enable_animation === 1;
    return (
        <div
            className={cn("flex w-full min-w-0 flex-col", className)}
            style={{ gap: T.titleGap }}
        >
            {data.description_section_title && (
                <div
                    className={cn(
                        "flex min-w-0 flex-col gap-0",
                        t,
                        textAlign === "center" && "mx-auto w-full",
                        titleWrapperClassName
                    )}
                    style={{ maxWidth: titleTextMaxW, width: "100%" }}
                >
                    <RevealOnScroll delay={200} enableAnimation={isAnimationEnabled}>
                    <span
                        className={cn(T.preTitle.className, "whitespace-pre-wrap")}
                        style={{
                            color: data.description_section_pre_title_color || "#FFFFFF",
                            fontFamily: "var(--font-google-sans)",
                        }}
                    >
                        {data.description_section_pre_title}
                    </span>
                    </RevealOnScroll>

                    <RevealOnScroll delay={210} enableAnimation={isAnimationEnabled}>
                    <span
                        className={cn(T.title.className, "whitespace-pre-wrap")}
                        style={{
                            color: data.description_section_title_color || "#FFFFFF",
                            fontFamily: "var(--font-merriweather)",
                        }}
                    >
                        {data.description_section_title} 
                    </span>
                    </RevealOnScroll>
                </div>
            )}
            {data.description_section_description && (
                <RevealOnScroll delay={220} enableAnimation={isAnimationEnabled}>
                <p
                    className={cn(T.body.className, t, descriptionClassName)}
                    style={{
                        color: data.description_section_description_text_color || "rgba(255,255,255,0.7)",
                        fontFamily: "var(--font-google-sans)",
                    }}
                >
                    {data.description_section_description}
                </p>
                </RevealOnScroll>
            )}
        </div>
    );
};

const Leaders1CarouselStage: FC<{
    activeLeaders: Leaders1MetadataItem[];
    currentIndex: number;
    band: Leaders1Band;
    minHeight: number;
}> = ({ activeLeaders, currentIndex, band, minHeight }) => {
    if (activeLeaders.length === 0) return null;
    return (
        <div
            className="relative flex w-full max-w-full justify-center overflow-visible *:pointer-events-auto pointer-events-none"
            style={{ minHeight }}
        >
            {activeLeaders.map((item, idx) => {
                const n = activeLeaders.length;
                const prevIdx = (currentIndex - 1 + n) % n;
                const nextIdx = (currentIndex + 1) % n;
                const position =
                    idx === currentIndex ? 0 : idx === prevIdx ? -1 : idx === nextIdx ? 1 : 2;
                return (
                    <LeaderCard
                        key={item.id ?? idx}
                        item={item}
                        position={position}
                        band={band}
                    />
                );
            })}
        </div>
    );
};

const Leaders1Mobile: FC<{
    data: Leaders1Parsed;
    descriptionBgStyle: CSSProperties | undefined;
    activeLeaders: Leaders1MetadataItem[];
    currentIndex: number;
}> = ({ data, descriptionBgStyle, activeLeaders, currentIndex }) => {
    const L = LEADERS1_LAYOUT.xs;
    /** Y where the center card’s bottom edge is (center at container top, height `centerH`). */
    const centerCardBottomY = L.centerH / 2;
    /** Reserves in-flow space so the copy starts below the center card + gap; cards are absolutely positioned. */
    const textStartOffsetY = centerCardBottomY + L.mobileDescriptionGapAfterCards;
    const purpleBoxStyle: CSSProperties = {
        ...descriptionBgStyle,
        height: L.mobilePurpleH,
        minHeight: L.mobilePurpleH,
        maxHeight: L.mobilePurpleH,
    };
    return (
        <div
            className="flex w-full min-w-0 flex-col sm:hidden"
            style={{ paddingTop: L.xsSectionHeaderClearance }}
        >
            {/*
              Cards layer: `absolute top-0` on the mobile block so LeaderCard (top+translate -50% -50%)
              ties the **center of the center card** to the **top-center of this container** (Figma).
              Upward bleed sits in the parent’s top padding. Never set overflow:hidden on this shell.
            */}
            <div
                className="relative flex w-full min-h-0 flex-1 flex-col overflow-visible rounded-[28px]"
                style={purpleBoxStyle}
            >
                <div
                    className="pointer-events-none absolute top-0 right-0 left-0 z-20 w-full min-w-0 overflow-visible *:pointer-events-auto"
                    style={{ minHeight: centerCardBottomY }}
                >
                    <Leaders1CarouselStage
                        activeLeaders={activeLeaders}
                        currentIndex={currentIndex}
                        band="xs"
                        minHeight={centerCardBottomY}
                    />
                </div>
                <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
                    <div
                        className="w-full shrink-0"
                        style={{ height: textStartOffsetY }}
                        aria-hidden
                    />
                    <div className="relative z-10 min-h-0 w-full min-w-0 flex-1 overflow-y-auto px-5 pb-[40px]">
                        <Leaders1DescriptionCopy
                            band="xs"
                            data={data}
                            className="mx-auto w-full max-w-[303px]"
                            textAlign="center"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const Leaders1Desktop: FC<{
    data: Leaders1Parsed;
    descriptionBgStyle: CSSProperties | undefined;
    activeLeaders: Leaders1MetadataItem[];
    currentIndex: number;
    band: Leaders1Band;
}> = ({ data, descriptionBgStyle, activeLeaders, currentIndex, band }) => {
    const L = LEADERS1_LAYOUT[band];
    const isAnimationEnabled =
        data.enable_animation === true || data.enable_animation === 1;
    const purpleStyle: CSSProperties = {
        height: L.panelStripH,
        borderRadius: L.panelRadius,
        ...descriptionBgStyle,
    };
    return (
        <div
            className="relative hidden w-full overflow-visible sm:block"
            style={{ minHeight: L.stageMinH, marginTop: -L.desktopBlockLift }}
        >
            <div
                className="pointer-events-none absolute top-1/2 right-0 left-0 z-0 w-full -translate-y-1/2"
                style={purpleStyle}
                aria-hidden
            />
            <div
                className="absolute top-1/2 left-0 z-[1] -translate-y-1/2 pr-5"
                style={{
                    paddingLeft: L.desktopTextPl,
                    maxWidth: L.desktopDescriptionMaxW,
                    width: "100%",
                }}
            >
                <Leaders1DescriptionCopy
                    band={band}
                    data={data}
                    textAlign="left"
                />
            </div>
            <div
                className="pointer-events-none absolute top-1/2 right-0 z-[2] flex w-1/2 min-w-0 max-w-[50%] -translate-y-1/2 items-center justify-end *:pointer-events-auto sm:pl-2"
            >
                <RevealOnScroll className="w-full" y={0} x={100} scaleStart={0.5} delay={200} enableAnimation={isAnimationEnabled}>

                <Leaders1CarouselStage
                    activeLeaders={activeLeaders}
                    currentIndex={currentIndex}
                    band={band}
                    minHeight={L.stageMinH}
                />
                </RevealOnScroll>
            </div>
        </div>
    );
};

export const Leaders1: FC<any> = (props) => {
    const actualData = props?.data?.data || props?.data || props;

    /*
      `useMemo`, not a bare call: `safeParse` walks the whole section payload, and this
      component re-renders on every carousel tick. Re-parsing identical data on each of
      those was pure waste.
    */
    const parsedResult = useMemo(
        () => Leaders1DataSchema.safeParse(actualData),
        [actualData]
    );

    /*
      NO EARLY RETURN ABOVE THIS LINE.

      The bail-out for a failed parse lives below every hook (just above the `return`).
      React identifies hooks purely by call order, so bailing here would skip the six
      hooks that follow and, the moment `parsedResult.success` differed between two
      renders of the same mounted component, React would throw "Rendered fewer hooks
      than expected" and take the tree down with it.

      `parsedData` is therefore undefined on a failed parse and everything derived from
      it falls back to empty, so the hooks below run harmlessly.
    */
    const parsedData = parsedResult.success ? parsedResult.data : undefined;

    const band = useLeaders1Band();
    const isNarrow = useIsMobile(LEADERS1_BP.sm);

    const activeLeaders = useMemo(() => {
        const list = (parsedData?.metadata || []).filter(
            (m: Leaders1MetadataItem) => m.meta_status !== 0
        );
        return [...list].sort(
            (a: Leaders1MetadataItem, b: Leaders1MetadataItem) =>
                (a.meta_priority ?? 0) - (b.meta_priority ?? 0)
        );
    }, [parsedData?.metadata]);

    const scrollFrequencyMs = parsedData?.scroll_frequency ?? 3000;
    const [currentIndex, setCurrentIndex] = useState(0);
    const sectionRef = useRef<HTMLElement>(null);
    const inView = useIsInView(sectionRef);

    /*
      `inView` is part of the guard on purpose. This section had no pause of any kind —
      no hover, no visibility — so the interval ran from mount until unmount, re-rendering
      the whole carousel (stage + four LeaderCards + their RevealOnScroll wrappers, ~8-14ms
      a commit in the Profiler) every 3s for content well below the fold.
    */
    useEffect(() => {
        if (activeLeaders.length <= 1 || !inView) return;
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % activeLeaders.length);
        }, scrollFrequencyMs);
        return () => clearInterval(interval);
    }, [activeLeaders.length, scrollFrequencyMs, inView]);

    // The bail-out, deliberately BELOW every hook (see the note above `parsedData`).
    if (!parsedData) {
        if (!parsedResult.success) {
            console.log("leaders 1 validation error", parsedResult.error);
        }
        return null;
    }

    const bgColor = formatBackgroundStyle(parsedData, (isNarrow ?? true) as boolean, "#ffffff");

    const descriptionBgStyle: CSSProperties | undefined =
        parsedData.description_box_gradient_angle &&
        parsedData.description_box_start_color &&
        parsedData.description_box_end_color
            ? {
                  background: `linear-gradient(${parsedData.description_box_gradient_angle}, ${parsedData.description_box_start_color}, ${parsedData.description_box_end_color})`,
              }
            : parsedData.description_box_start_color
              ? { backgroundColor: parsedData.description_box_start_color }
              : { background: "linear-gradient(180deg, #342B7C 0%, #02A7B6 100%)" };

    const showMobile = isNarrow !== false;

    return (
        <section
            ref={sectionRef}
            className={cn(
                "relative flex w-full justify-center overflow-x-visible",
                "px-[24px] py-[40px]",
                "sm:px-[60px] sm:py-[40px] sm:pb-0",
                "md:px-[60px] md:py-[60px] md:pb-0",
                "lg:px-[80px] lg:py-[60px] lg:pb-0",
                "xl:px-[140px] xl:py-[80px] xl:pb-0"
            )}
            style={bgColor}
        >
            <div
                className={cn(
                    "mx-auto flex w-full max-w-full flex-col",
                    "sm:w-[960px] sm:gap-[90px]",
                    "md:w-[1110px] md:gap-[96px]",
                    "lg:w-[1270px] lg:gap-[129px]",
                    "xl:w-[1470px] xl:gap-[146px]"
                )}
            >
                <Leaders1SectionHeader data={parsedData} />

                {showMobile ? (
                    <Leaders1Mobile
                        data={parsedData}
                        descriptionBgStyle={descriptionBgStyle}
                        activeLeaders={activeLeaders}
                        currentIndex={currentIndex}
                    />
                ) : (
                        <Leaders1Desktop
                            data={parsedData}
                            descriptionBgStyle={descriptionBgStyle}
                            activeLeaders={activeLeaders}
                            currentIndex={currentIndex}
                            band={band}
                        />
                )}
            </div>
        </section>
    );
};
